/**
 * ManualAccountModal Component
 * Modal form for creating and editing manual accounts (not linked to Plaid)
 */
import { useState, useEffect, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Alert from '@/components/ui/Alert';
import AccountTypeSelector from './AccountTypeSelector';
import { accountsService } from '@/services/accounts';
import type { AccountWithConnection } from '@/services/accounts';
import { ApiClientError } from '@/services/api';
import { accountKeys } from '@/hooks/useAccounts';
import type { AccountType, AccountSubtype } from '@/types';

export interface ManualAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  account?: AccountWithConnection; // If editing existing account
  onSuccess?: () => void;
}

// Currency options
const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD - US Dollar' },
  { value: 'EUR', label: 'EUR - Euro' },
  { value: 'GBP', label: 'GBP - British Pound' },
  { value: 'CAD', label: 'CAD - Canadian Dollar' },
  { value: 'AUD', label: 'AUD - Australian Dollar' },
  { value: 'JPY', label: 'JPY - Japanese Yen' },
  { value: 'CHF', label: 'CHF - Swiss Franc' },
];

interface FormData {
  name: string;
  type: AccountType;
  subtype: AccountSubtype;
  currency: string;
  initialBalance: string;
  balanceDate: string;
  notes: string;
}

/**
 * Get today's date in local timezone as YYYY-MM-DD string
 */
function getLocalDateString(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const getDefaultFormData = (account?: AccountWithConnection): FormData => ({
  name: account?.name ?? '',
  type: account?.type ?? 'other',
  subtype: (account?.subtype as AccountSubtype) ?? 'other_asset',
  currency: account?.currency ?? 'USD',
  initialBalance: '',
  balanceDate: getLocalDateString(),
  notes: account?.notes ?? '',
});

export default function ManualAccountModal({
  isOpen,
  onClose,
  account,
  onSuccess,
}: ManualAccountModalProps) {
  const queryClient = useQueryClient();
  const isEditing = !!account;

  const [formData, setFormData] = useState<FormData>(getDefaultFormData(account));
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens/closes or account changes
  useEffect(() => {
    if (isOpen) {
      setFormData(getDefaultFormData(account));
      setError(null);
    }
  }, [isOpen, account]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: accountsService.createManual,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.all });
      onSuccess?.();
      handleClose();
    },
    onError: (err) => {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to create account. Please try again.');
      }
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof accountsService.updateManual>[1] }) =>
      accountsService.updateManual(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.all });
      onSuccess?.();
      handleClose();
    },
    onError: (err) => {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to update account. Please try again.');
      }
    },
  });

  const handleClose = () => {
    setFormData(getDefaultFormData());
    setError(null);
    onClose();
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate required fields
    if (!formData.name.trim()) {
      setError('Account name is required');
      return;
    }

    if (isEditing && account) {
      updateMutation.mutate({
        id: account.id,
        data: {
          name: formData.name.trim(),
          type: formData.type,
          subtype: formData.subtype,
          notes: formData.notes.trim() || undefined,
        },
      });
    } else {
      // Validate balance date if initial balance is provided
      const initialBalance = formData.initialBalance.trim()
        ? parseFloat(formData.initialBalance)
        : undefined;

      if (initialBalance !== undefined && !formData.balanceDate) {
        setError('Balance date is required when setting an initial balance');
        return;
      }

      // Validate balance date is not in the future
      if (formData.balanceDate) {
        const balanceDate = new Date(formData.balanceDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (balanceDate > today) {
          setError('Balance date cannot be in the future');
          return;
        }
      }

      createMutation.mutate({
        name: formData.name.trim(),
        type: formData.type,
        subtype: formData.subtype,
        currency: formData.currency,
        initialBalance,
        balanceDate: initialBalance !== undefined ? formData.balanceDate : undefined,
        notes: formData.notes.trim() || undefined,
      });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="manual-account-modal-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h2
          id="manual-account-modal-title"
          className="text-lg font-semibold text-gray-900 mb-4"
        >
          {isEditing ? 'Edit Manual Account' : 'Add Manual Account'}
        </h2>

        {!isEditing && (
          <p className="text-sm text-gray-600 mb-4">
            Create an account to track assets or liabilities that aren&apos;t connected to a bank.
          </p>
        )}

        {error && (
          <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Account Name"
            id="account-name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Rental Property - 123 Main St"
            autoFocus
          />

          <AccountTypeSelector
            type={formData.type}
            subtype={formData.subtype}
            onTypeChange={(type) => setFormData((prev) => ({ ...prev, type }))}
            onSubtypeChange={(subtype) => setFormData((prev) => ({ ...prev, subtype }))}
          />

          {!isEditing && (
            <>
              <Select
                label="Currency"
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                options={CURRENCY_OPTIONS}
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Current Value"
                  type="number"
                  step="0.01"
                  value={formData.initialBalance}
                  onChange={(e) => setFormData({ ...formData, initialBalance: e.target.value })}
                  placeholder="0.00"
                  hint="Leave empty if unknown"
                />
                <Input
                  label="As of Date"
                  type="date"
                  value={formData.balanceDate}
                  onChange={(e) => setFormData({ ...formData, balanceDate: e.target.value })}
                  max={getLocalDateString()}
                  disabled={!formData.initialBalance}
                />
              </div>
            </>
          )}

          <Input
            label="Notes (optional)"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="e.g., Purchased January 2024"
          />

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isPending}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={isPending}
              className="flex-1"
            >
              {isEditing ? 'Save Changes' : 'Add Account'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
